import type { ReactNode } from 'react';
import { Link } from '@customerio/pluma-components/react';
import { actionDisplayInfo } from '@/features/workflow-builder/ui';
import { useListWorkspaces } from '@/features/workspaces';
import { ACTIVITY_LOG_SOURCE } from '@/utils/activity-log-source-types';
import { isEmptyString } from '@/utils/string';
import type { ActivityLog, ActivityLogSource } from '../types';
import {
  actionComposerHref,
  campaignHref,
  isFromOtherWorkspace,
  journeyHref,
  llmActionHref,
  normalizedActionType,
  otherWorkspaceHref,
} from './log-source-links';

type CampaignClauseProps = {
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
};

/** "in the "<automation>" automation", or "in a deleted automation" once the API sends no name. */
export function CampaignClause({ source, workspaceId, campaignSingular }: CampaignClauseProps) {
  const term = campaignSingular.toLowerCase();
  if (isEmptyString(source.campaign_name ?? '')) {
    return <>in a deleted {term}</>;
  }
  return (
    <>
      in the <Link href={campaignHref(workspaceId, source.campaign_id)}>"{source.campaign_name}"</Link> {term}
    </>
  );
}

type OtherWorkspaceSourceProps = { source: ActivityLogSource; environmentId: number };

/**
 * A source whose automation lives in another workspace, so this workspace's API has no name for it.
 * The workspace list holds the workspaces the user can open; one missing from it is not accessible.
 * If the list fails to load, access is unknown, so the link stays without a name or an access claim.
 */
function OtherWorkspaceSource({ source, environmentId }: OtherWorkspaceSourceProps) {
  const workspaces = useListWorkspaces();
  if (workspaces.isPending) return null;
  const href = otherWorkspaceHref(source, environmentId);
  if (workspaces.isError) {
    return (
      <Link href={href} variant="secondary" target="_blank">
        Another workspace
      </Link>
    );
  }
  const workspace = workspaces.data?.find((w) => w.id === environmentId);
  if (workspace == null) {
    return <>Another workspace. Contact your admin for access to this workspace</>;
  }
  return (
    <Link href={href} variant="secondary" target="_blank">
      Workspace "{workspace.name}"
    </Link>
  );
}

type OtherWorkspaceOrProps = { source: ActivityLogSource; workspaceId: string; children: ReactNode };

/**
 * Renders the other workspace in place of the whole source when the automation lives there (Ember
 * `action-link`), else the source's own wording. Every automation-bearing source goes through this,
 * because the API sends no automation name for another workspace and the wording would read "deleted".
 */
export function OtherWorkspaceOr({ source, workspaceId, children }: OtherWorkspaceOrProps) {
  if (source.environment_id != null && isFromOtherWorkspace(source, workspaceId)) {
    return <OtherWorkspaceSource source={source} environmentId={source.environment_id} />;
  }
  return <>{children}</>;
}

/** Whether the source names an action by type or name; an id alone leaves nothing to show. */
export function hasAction(source: ActivityLogSource): boolean {
  return !isEmptyString(source.action_type ?? '') || !isEmptyString(source.action_name ?? '');
}

type ActionNameProps = { log: ActivityLog; source: ActivityLogSource; workspaceId: string };

/** The action's quoted name, else its type's display name; linked to the action when it has an address. */
function ActionName({ log, source, workspaceId }: ActionNameProps) {
  const hasName = !isEmptyString(source.action_name ?? '');
  const label = hasName
    ? `"${source.action_name}"`
    : actionDisplayInfo(normalizedActionType(source.action_type ?? '')).name;
  const href =
    source.type === ACTIVITY_LOG_SOURCE.LLMAction
      ? llmActionHref(source, workspaceId)
      : actionComposerHref(log, source, workspaceId);
  if (href == null) return <>{label}</>;
  return (
    <Link href={href} variant="secondary">
      {label}
    </Link>
  );
}

type JourneyLinkProps = { source: ActivityLogSource; workspaceId: string };

function JourneyLink({ source, workspaceId }: JourneyLinkProps) {
  const href = journeyHref(source, workspaceId);
  if (href == null) return null;
  return (
    <>
      {' '}
      from{' '}
      <Link href={href} variant="secondary">
        this journey
      </Link>
    </>
  );
}

type ActionSourceProps = {
  log: ActivityLog;
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
};

/**
 * A step in an automation as a source: `"<action>" action [from this journey] in the "<automation>"
 * automation`. Mirrors Ember's `formatted-log-source/action-link`; a source from another workspace
 * becomes a link to that workspace instead.
 */
export function ActionSource({ log, source, workspaceId, campaignSingular }: ActionSourceProps) {
  return (
    <OtherWorkspaceOr source={source} workspaceId={workspaceId}>
      <SameWorkspaceActionSource
        log={log}
        source={source}
        workspaceId={workspaceId}
        campaignSingular={campaignSingular}
      />
    </OtherWorkspaceOr>
  );
}

function SameWorkspaceActionSource({ log, source, workspaceId, campaignSingular }: ActionSourceProps) {
  const action = hasAction(source) ? <ActionName log={log} source={source} workspaceId={workspaceId} /> : <>deleted</>;
  if (isEmptyString(source.campaign_name ?? '')) {
    return (
      <>
        {action} action in a deleted {campaignSingular.toLowerCase()}
      </>
    );
  }
  return (
    <>
      {action} action
      <JourneyLink source={source} workspaceId={workspaceId} />{' '}
      <CampaignClause source={source} workspaceId={workspaceId} campaignSingular={campaignSingular} />
    </>
  );
}
